import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {flagLabelsApi, type FlagColor, type FlagLabel} from './flags';
import {isItemsPageQuery} from './hooks';
import {itemsApi, type ItemsPage} from './items';

// The caller's own names for their flag colours. Cheap and rarely changes, so it is fetched once
// and shared by every picker on the page.
export const useFlagLabels = () =>
  useQuery({
    queryKey: ['me', 'flag-labels'] as const,
    queryFn: async () => flagLabelsApi.list(),
    staleTime: 5 * 60_000
  });

export const useSetFlagLabels = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (labels: FlagLabel[]) => flagLabelsApi.replace(labels),
    onSuccess(_data, labels) {
      qc.setQueryData(['me', 'flag-labels'], labels);
    }
  });
};

/**
 Sets (or clears) the caller's note on their flag for one item, patched into every cached item page
 first like useSetItemFlags, and rolled back if the write fails.
 */
export const useSetFlagNote = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({itemId, note}: {itemId: string; note: string | null}) =>
      itemsApi.setFlagNote(itemId, note),
    async onMutate({itemId, note}) {
      await qc.cancelQueries({predicate: isItemsPageQuery});
      const previous = qc.getQueriesData<ItemsPage>({predicate: isItemsPageQuery});
      const trimmed = note?.trim() ? note.trim() : null;
      qc.setQueriesData<ItemsPage>({predicate: isItemsPageQuery}, (page) =>
        page
          ? {
              ...page,
              results: page.results.map((item) =>
                item.id === itemId ? {...item, flag_note: trimmed} : item
              )
            }
          : page
      );
      return {previous};
    },
    onError(_error, _variables, context) {
      for (const [key, data] of context?.previous ?? []) {
        qc.setQueryData(key, data);
      }
    }
  });
};

/**
 Flags one item with a note in a single write ("Flag with note…"), patched into every cached item
 page first like useSetItemFlags and rolled back if the write fails.
 */
export const useFlagWithNote = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      itemId,
      color,
      note
    }: {
      itemId: string;
      color: FlagColor;
      note: string | null;
    }) => itemsApi.setFlags([itemId], color, note),
    async onMutate({itemId, color, note}) {
      await qc.cancelQueries({predicate: isItemsPageQuery});
      const previous = qc.getQueriesData<ItemsPage>({predicate: isItemsPageQuery});
      qc.setQueriesData<ItemsPage>({predicate: isItemsPageQuery}, (page) =>
        page
          ? {
              ...page,
              results: page.results.map((item) =>
                item.id === itemId
                  ? {...item, flag: color, flag_note: note ?? item.flag_note ?? null}
                  : item
              )
            }
          : page
      );
      return {previous};
    },
    onError(_error, _variables, context) {
      for (const [key, data] of context?.previous ?? []) {
        qc.setQueryData(key, data);
      }
    }
  });
};
